import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getDb, NOTICES_COLLECTION } from "./firebase";

/**
 * Site-wide notices shown to every visitor.
 *
 * STORAGE: Firestore collection `notices`, world-readable. Writes require an
 * authenticated admin (Firebase Auth), enforced by security rules — the client
 * cannot grant itself permission.
 *
 * Images are embedded as base64 data URIs rather than uploaded to Firebase
 * Storage, which would require upgrading the project to the Blaze plan. Images
 * are compressed and resized in the browser to stay well under Firestore's 1 MB
 * per-document limit.
 */

export interface Notice {
  /** Firestore document id. */
  id: string;
  /** Set false to hide without deleting. */
  active: boolean;
  /** ISO timestamp; drives the auto-expiry countdown. */
  publishedAt: string;
  /** Days until it hides itself. Ignored when expiresAt is set. */
  expiresInDays: number;
  /**
   * Explicit expiry instant (ISO). Set when the admin picks a calendar date,
   * in which case it wins over expiresInDays.
   */
  expiresAt?: string;
  /** Short bold lead-in. */
  title: string;
  /** Body copy. Shown above the image when both are present. */
  body: string;
  /** Optional base64 data URI, shown alongside the title and body. */
  imageUrl?: string;
  /** Visual weight. */
  tone: "info" | "warning" | "critical";
  /**
   * How it is presented:
   *   banner — strip across the top of the page (default)
   *   popup  — centred modal over a dimmed backdrop, for artwork that needs
   *            to be seen whole (posters, announcements)
   */
  display: "banner" | "popup";
}

export const BLANK_NOTICE: Omit<Notice, "id"> = {
  active: true,
  publishedAt: new Date().toISOString(),
  expiresInDays: 7,
  title: "Token transfers are temporarily paused.",
  body:
    "Our automated transfer service is experiencing a disruption. We're working " +
    "with Chainlink to restore it. Your tokens and balances are safe.",
  tone: "warning",
  display: "banner",
};

/** True when a notice is active and inside its expiry window. */
export function expiryTime(notice: Notice): number {
  if (notice.expiresAt) {
    const t = new Date(notice.expiresAt).getTime();
    if (Number.isFinite(t)) return t;
  }
  const published = new Date(notice.publishedAt).getTime();
  if (!Number.isFinite(published)) return 0;
  return published + notice.expiresInDays * 86400000;
}

export function isNoticeVisible(notice: Notice | null): boolean {
  if (!notice || !notice.active) return false;
  return Date.now() < expiryTime(notice);
}

/** Whole days left before auto-expiry; 0 once elapsed. */
export function daysRemaining(notice: Notice): number {
  return Math.max(0, Math.ceil((expiryTime(notice) - Date.now()) / 86400000));
}

/** End of the given local day (23:59:59.999) as an ISO string. */
export function endOfDayIso(dateYmd: string): string {
  const [y, m, d] = dateYmd.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

/** yyyy-mm-dd for a date input, from an ISO instant. */
export function isoToDateInput(iso?: string): string {
  if (!iso) return "";
  const dt = new Date(iso);
  if (!Number.isFinite(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function normalise(id: string, data: any): Notice {
  // publishedAt may be an ISO string or a Firestore Timestamp depending on
  // whether it was written by this app or edited in the console.
  let publishedAt = data?.publishedAt;
  if (publishedAt instanceof Timestamp) publishedAt = publishedAt.toDate().toISOString();
  if (typeof publishedAt !== "string") publishedAt = new Date(0).toISOString();

  return {
    id,
    active: Boolean(data?.active),
    publishedAt,
    expiresInDays: Number(data?.expiresInDays) || 7,
    expiresAt: typeof data?.expiresAt === "string" ? data.expiresAt : undefined,
    title: String(data?.title ?? ""),
    body: String(data?.body ?? ""),
    imageUrl: data?.imageUrl || undefined,
    tone: ["info", "warning", "critical"].includes(data?.tone) ? data.tone : "warning",
    display: data?.display === "popup" ? "popup" : "banner",
  };
}

/**
 * Every notice, newest first. Failures resolve to an empty list rather than
 * throwing — a notice outage must never stop the site rendering.
 */
export async function fetchNotices(): Promise<Notice[]> {
  const db = getDb();
  if (!db) return [];
  try {
    const snap = await getDocs(collection(db, NOTICES_COLLECTION));
    return snap.docs
      .map((d) => normalise(d.id, d.data()))
      .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  } catch (error) {
    console.warn("[NoticeService] could not load notices", error);
    return [];
  }
}

/** Only those a visitor should currently see. */
export async function fetchVisibleNotices(): Promise<Notice[]> {
  return (await fetchNotices()).filter(isNoticeVisible);
}

/**
 * Create or update. Requires an authenticated admin.
 *
 * Firestore rejects `undefined` field values outright, so optional fields must
 * be stripped rather than written as undefined — otherwise saving a notice with
 * no image throws "Unsupported field value: undefined".
 */
export async function saveNotice(notice: Notice): Promise<void> {
  const db = getDb();
  if (!db) throw new Error("Firebase unavailable.");
  const { id, ...rest } = notice;
  const data = Object.fromEntries(
    Object.entries(rest).filter(([, v]) => v !== undefined)
  );
  await setDoc(doc(db, NOTICES_COLLECTION, id), data);
}

export async function deleteNotice(id: string): Promise<void> {
  const db = getDb();
  if (!db) throw new Error("Firebase unavailable.");
  await deleteDoc(doc(db, NOTICES_COLLECTION, id));
}

/** Stable-ish id without pulling in a uuid dependency. */
export function newNoticeId(): string {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Resize + compress an image in the browser and return a base64 data URI.
 *
 * Firestore documents cap at 1 MB and base64 inflates by ~33%, so the source is
 * scaled to fit maxWidth and re-encoded as JPEG until it fits the budget.
 * Throws if it cannot get small enough, rather than silently saving something
 * that would be rejected on write.
 */
export async function imageToDataUri(
  file: File,
  maxWidth = 1200,
  maxBytes = 700 * 1024,
  maxHeight = 1600
): Promise<string> {
  const bitmap = await createImageBitmap(file);
  // Constrain BOTH dimensions. Scaling on width alone leaves a tall poster
  // (e.g. 887x1774) at full size, forcing quality down instead of resolution
  // and making fine text mushy.
  const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process image.");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  // PNG-with-transparency would lose its alpha as JPEG; keep PNG if it fits.
  const asPng = canvas.toDataURL("image/png");
  if (asPng.length <= maxBytes) return asPng;

  for (const quality of [0.85, 0.7, 0.55, 0.4, 0.28]) {
    const uri = canvas.toDataURL("image/jpeg", quality);
    if (uri.length <= maxBytes) return uri;
  }
  throw new Error(
    "Image is too large even after compression. Try a smaller or simpler image."
  );
}

/**
 * Per-device dismissal.
 *
 * Deliberately temporary: dismissing hides a notice for the rest of the
 * session plus a short cool-off, then it returns. A permanent dismissal would
 * mean someone who closed an outage banner once would never see it again, even
 * while the outage continued.
 *
 * Stored as { [id]: { at: <dismiss time>, pub: <publishedAt> } } so re-publishing
 * a notice also brings it back immediately.
 */
const DISMISS_KEY = "cvt_notices_dismissed";

/**
 * How long a dismissal sticks, per display type.
 *
 * Popups are announcements the admin wants seen, so they come back quickly —
 * practically, anyone who closes the tab and returns later gets it again. The
 * 15-minute floor just stops it re-appearing mid-session the moment it is
 * closed. Banners are less intrusive and stay hidden far longer.
 */
export const DISMISS_MINUTES = { popup: 15, banner: 12 * 60 };

type DismissRecord = Record<string, { at: number; pub: string }>;

function readDismissed(): DismissRecord {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
    // Tolerate the older string-valued format rather than throwing on it.
    const out: DismissRecord = {};
    for (const [id, v] of Object.entries(raw)) {
      if (typeof v === "string") out[id] = { at: 0, pub: v };
      else if (v && typeof v === "object") out[id] = v as { at: number; pub: string };
    }
    return out;
  } catch {
    return {};
  }
}

export function dismissNotice(notice: Notice): void {
  try {
    const all = readDismissed();
    all[notice.id] = { at: Date.now(), pub: notice.publishedAt };
    localStorage.setItem(DISMISS_KEY, JSON.stringify(all));
  } catch {
    /* private browsing — dismissal just won't persist */
  }
}

export function isDismissed(notice: Notice): boolean {
  const rec = readDismissed()[notice.id];
  if (!rec) return false;
  // Re-publishing resets the dismissal.
  if (rec.pub !== notice.publishedAt) return false;
  const minutes =
    notice.display === "popup" ? DISMISS_MINUTES.popup : DISMISS_MINUTES.banner;
  return Date.now() - rec.at < minutes * 60000;
}
